// src/oura/screens/BusinessOverview.tsx
//
// The staff Overview for one business: an index, not a report. The header
// sentence carries the verdict, four tiles carry one number each and open the
// screen that holds the detail, and the row under them says what moves next
// and what needs a person.
//
// The cards this screen used to hold all have a home elsewhere, with their
// refresh controls: the places and competitors on Visibility, Site Health, AI
// Readability, the crawl and strengths on Site, Traffic on Search
// performance, and the recommendations on Fixes.

import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import { useNavigate } from "react-router-dom";
import { useDelivery } from "../delivery/useDelivery";
import { DELIVERY_ON } from "../delivery/flag";
import type {
    AeoAiVisibility,
    AeoGrounding,
    AeoLatestAttempt,
    AeoScoreHistoryEntry,
    AeoSearchPerformance,
} from "@service/aeo";
import { isClientReviewLive, type ClientPlanItem } from "@service/aeo/clientReview";
import type { Business, ScoreSnapshot } from "../contract";
import type { BusinessAudit } from "../audit";
import type { DataConnector } from "../connectors";
import type { GscState } from "../gscState";
import type { TrendBreak } from "../trendBreak";
import { getAudit } from "../audit";
import { placesFromLive } from "../places";
import { getTracker as getFixtureTracker } from "../tracker";
import { makeTrackerScope, useTaskPlacement, useTaskStatuses } from "../trackerState";
import { isOuraProd, useOuraRole } from "../role";
import { ouraHref, showOnboarding } from "../nav";
import { ScanAuditBadge } from "../components/ScanAuditBadge";
import { todayKey } from "../components/fixes/model";
import { useOverviewData } from "../hooks/useOverviewData";
import { buildVisModel, visibilityInputsFromAssessment, type LatestAssessment } from "../visibility/fromLive";
import { visibilityFixture } from "../visibility/fixtures";
import { trendDelta } from "../visibility/model";
import { cn } from "../lib/cn";
import { ArrowLink, Bento, BentoStack, FoldScope, PageActions, PageSentence, Surface, TYPE } from "@oura/ui";
import {
    asksWaitingOnClient,
    attentionRows,
    couldNotRead,
    isLate,
    overviewSentence,
    overviewDetail,
    recommendationRows,
    repliesWaitingOnUs,
    scanVerdict,
    topRival,
    whatNext,
    nextFromDelivery,
    deliveryAttention,
    withSessionEdits,
    type Rating,
} from "../overview/model";
import { OverviewTiles, type AiHeadline, type MonthTileState } from "../overview/OverviewTiles";
import { WhatNextCard } from "../overview/WhatNextCard";
import { AttentionCard } from "../overview/AttentionCard";
import { NotesCard } from "../overview/NotesCard";
import { measuredItems, planDone } from "../merchant/model";
import { groupRequests } from "../components/client/changeRequests";
import { HubNextSlot } from "../overview/HubEmpty";
import { useHub } from "../overview/useHubStage";

/**
 * The same gate BusinessDetail puts on the Client section. The client-review
 * service answers from a local mock whenever its live flag is unset, so every
 * read of notes, asks or the client's plan follows it.
 */
const showClientApprovals = isClientReviewLive || !isOuraProd();

/**
 * A sample business with no Visibility fixture has no scan result to report:
 * its question rows are generated, and Visibility says so. The Overview says
 * the same rather than count them.
 */
const NO_VIS_RESULT: AiHeadline = { named: 0, of: 0, absent: "No visibility result from the last scan." };

/**
 * The month's count over measured work only, as the merchant's Home and Work
 * count it (staging 3fdc24e6): a `reported_by_hand` line is never "done", so
 * counting it would cap the tile below its own total, and staff would read a
 * different "N of M" from the merchant for the same month.
 */
function monthOfPlan(items: ClientPlanItem[], lateQuestions: number): MonthTileState {
    const measured = measuredItems(items);
    return {
        kind: "plan",
        done: planDone(measured),
        total: measured.length,
        byHand: items.length - measured.length,
        lateQuestions,
    };
}

export function BusinessOverview({
    snapshot,
    business,
    liveAudit,
    grounding,
    searchPerformance,
    aiVisibilityRaw,
    trendBreak,
    assessment,
    rating,
    gscState,
    connectors,
    ga4Connector,
    scanHistory,
    latestAttempt,
    nextScanAt,
    assessmentId,
    viewingPast,
    onBackToLatest,
}: {
    snapshot: ScoreSnapshot;
    business: Business;
    /** Real data (ourteam-frontend#3160). Absent on a fixture business, which
     *  reads the getAudit() sample instead. */
    liveAudit?: BusinessAudit;
    /** How the ChatGPT number was measured (#3784), for the instrument row. */
    grounding?: AeoGrounding | null;
    /** `results.searchPerformance`, for the Google search tile; on a fixture, the
     *  demo block its Search performance screen reads. */
    searchPerformance?: AeoSearchPerformance | null;
    /** Live only: the raw `results.aiVisibility`, for the places. */
    aiVisibilityRaw?: AeoAiVisibility | null;
    /** Live only: where the question set was re-authored (#3539). */
    trendBreak?: TrendBreak | null;
    /** Live only: the scan on screen, latest or past, as the detail carries it.
     *  Visibility builds its model from the same block. */
    assessment?: LatestAssessment | null;
    /** Live only: the rating and its cap, from `results.reportMetrics`. */
    rating?: Rating;
    /** Undefined until the connector catalog arrives, and on a fixture. */
    gscState?: GscState;
    /** The connector catalog, for the website platform. Undefined until it arrives. */
    connectors?: DataConnector[];
    /** Undefined: not known yet. Null: the catalog arrived without a GA4 entry. */
    ga4Connector?: DataConnector | null;
    /** Every terminal scan, newest first: the named count per scan rides on it. */
    scanHistory?: AeoScoreHistoryEntry[];
    /** A newer run that failed, when there is one. */
    latestAttempt?: AeoLatestAttempt | null;
    /** The next scheduled scan; null when auto-rescan is off. */
    nextScanAt?: string | null;
    /** The latest scan, which the plan hangs off. Absent on a fixture business and on a past scan. */
    assessmentId?: string;
    /** A past scan is on screen: the tiles follow it, and today's work does not. */
    viewingPast: boolean;
    onBackToLatest: () => void;
}) {
    const isFixture = !liveAudit;
    const audit = liveAudit ?? getAudit(business, snapshot);

    // Visibility's own inputs and model, so the AI tile states the count, the
    // change and the leading rival the Visibility screen states. A sample
    // business with a Visibility fixture goes through the same builder.
    const capturedAt = snapshot.capturedAt;
    const visInputs = useMemo(
        () => (isFixture
            ? visibilityFixture(business.id)
            : assessment
                ? visibilityInputsFromAssessment(assessment, { capturedAt, history: scanHistory ?? [] })
                : null),
        [isFixture, business.id, assessment, capturedAt, scanHistory],
    );
    const visModel = useMemo(
        () => (visInputs && (isFixture || visInputs.vis) ? buildVisModel(visInputs, null) : null),
        [visInputs, isFixture],
    );
    const fixtureVis = isFixture ? visInputs : null;
    const shownGrounding = fixtureVis ? fixtureVis.grounding : grounding;

    // The places, from payload-shaped blocks. A sample business without a
    // Visibility fixture has only its Search Console block: its AI side has
    // no result, so no count, rival or cap is read from it.
    const noVisResult = isFixture && !fixtureVis;
    const places = liveAudit
        ? placesFromLive(searchPerformance, aiVisibilityRaw, grounding, audit.aeoScoreAbsent ?? null, trendBreak)
        : placesFromLive(searchPerformance, fixtureVis?.vis, fixtureVis?.grounding, null, fixtureVis?.trendBreak);
    const headline: AiHeadline = visModel
        ? { named: visModel.headline.named, of: visModel.headline.of, absent: visModel.headline.absent }
        : noVisResult
            ? NO_VIS_RESULT
            : { named: places.assistants.named, of: places.assistants.of, absent: places.assistants.absentReason };
    // A sample business's rating is its snapshot level, with the cap its
    // Visibility fixture carries; without one there is no cap to state.
    const shownRating: Rating = isFixture
        ? { level: snapshot.level, cappedBy: fixtureVis?.levelCappedBy ?? null }
        : rating ?? { level: null, cappedBy: null };
    const verdict = scanVerdict(visModel?.headline ?? null, audit.aeoVerdict);
    const sentence = overviewSentence(headline, places.sources, shownRating);
    const sentenceDetail = overviewDetail(verdict, shownRating);
    const change = visModel ? trendDelta(visModel.trend, "all") : null;
    const navigate = useNavigate();
    const role = useOuraRole();
    const rival = visModel ? topRival(visModel.rivals) : null;

    // A sample business's plan as Fixes left it: the statuses, week moves
    // and removals made there this session, read through the same store so
    // coming back from Fixes shows what was changed. A live plan is read
    // back from the server with its edits saved.
    const trackerScope = makeTrackerScope(business.id, snapshot.capturedAt);
    const statusOf = useTaskStatuses(trackerScope);
    const { weekOf, isRemoved } = useTaskPlacement(trackerScope);
    const sampleTracker = useMemo(
        () => (isFixture ? getFixtureTracker(business, snapshot) : null),
        [isFixture, business, snapshot],
    );
    const fixtureTracker = sampleTracker ? withSessionEdits(sampleTracker, { statusOf, weekOf, isRemoved }) : null;
    const { plan, clientPage, notes, calls, changes, onboarding, timeline } = useOverviewData({
        businessId: business.id,
        assessmentId,
        fixtureTracker,
        clientReview: showClientApprovals,
        // Onboarding is hidden in production until its endpoints ship, and a
        // row here links straight into it — so this follows the same gate.
        onboarding: showOnboarding,
        active: !viewingPast,
    });

    const today = todayKey();
    const page = clientPage.status === "ready" ? clientPage.value : null;
    const tracker = plan.status === "ready" ? plan.value : null;
    // What the client started and we owe, by the Requests card's own rule: a
    // change until it is Done, a call until it is scheduled.
    const requests = useMemo(
        () => groupRequests(changes.status === "ready" ? changes.value : [], calls.status === "ready" ? calls.value : []).needsUs,
        [changes, calls],
    );
    // With Delivery on, each deliverable's next step is the plan (proposal v1.6).
    const delivery = useDelivery(business.id, business.name);
    const deliveryData = DELIVERY_ON && delivery.load.state === "ready" ? delivery.load.data : null;
    // With Delivery on, Fixes is retired: its steps never show here, contract or not.
    // Onboarding steps are work too: an open step is either ours to do or the
    // client's to answer, and until it is done the engagement cannot start. A
    // failed or still-loading read contributes no rows rather than claiming
    // onboarding is finished.
    // With the stage's Next card on top (New scan switch on), it alone names the
    // onboarding steps and the schedule (JOURNEY-SPEC v1.1 §2.2): one voice.
    const hub = useHub();
    const onboardingRecord = hub ? null : onboarding.status === "ready" ? onboarding.value : null;
    const planNext = whatNext(
        DELIVERY_ON ? null : tracker,
        page ? asksWaitingOnClient(page.packs) : [],
        today,
        requests,
        onboardingRecord,
    );
    const next = deliveryData ? nextFromDelivery(deliveryData, planNext, today) : planNext;

    // The month is the client's page: a page that could not be read says so,
    // and no page at all means nothing has been sold yet. Only our questions
    // are ever late on the client's side (a client task with no question is
    // just the client's turn).
    const month: MonthTileState | null = viewingPast
        ? null
        : DELIVERY_ON
          // Delivery's "N of M done", the same number on every screen.
          ? delivery.load.state === "ready"
              ? { kind: "plan", done: delivery.load.data.doneCount.done, total: delivery.load.data.doneCount.total, byHand: 0, lateQuestions: next.client.filter(isLate).length }
              : delivery.load.state === "loading"
                ? { kind: "loading" }
                : delivery.load.state === "failed"
                  ? { kind: "failed" }
                  : { kind: "none" }
          : clientPage.status === "off"
            ? { kind: "off" }
            : clientPage.status === "loading" || plan.status === "loading"
                ? { kind: "loading" }
                : clientPage.status === "failed"
                    ? { kind: "failed" }
                    : !page?.engagement || !page.plan
                      ? { kind: "none" }
                      : monthOfPlan(page.plan.items, next.client.filter(isLate).length);

    const attention = attentionRows({
        today,
        next,
        replies: page ? repliesWaitingOnUs(page.packs, page.threads) : [],
        grounding: shownGrounding,
        places,
        latestAttempt,
        gsc: gscState,
        ga4: ga4Connector,
        hasTraffic: !!audit.traffic,
        connectors,
        timeline: timeline.status === "ready" ? timeline.value : null,
        // Delivery's dates replace the Fixes steps' dates.
        tracker: DELIVERY_ON ? null : tracker,
        nextScanAt,
    })
        // The publish state is the Next card's while it shows it, so the same fact is never said twice.
        .filter((r) => !(hub?.next?.act?.kind === "timeline" && (r.key === "timeline-unpublished" || r.key === "timeline-pending")))
        .concat(deliveryData ? deliveryAttention(deliveryData) : []);
    const recommendations = recommendationRows(audit.recommendations, 3, DELIVERY_ON);
    const sources = [plan, clientPage, calls, changes, timeline];
    const unread = couldNotRead([
        ...(DELIVERY_ON ? (delivery.load.state === "failed" ? ["Delivery"] : []) : plan.status === "failed" ? ["the plan"] : []),
        ...(clientPage.status === "failed" ? ["the client's page"] : []),
        ...(calls.status === "failed" ? ["the call requests"] : []),
        ...(changes.status === "failed" ? ["the change requests"] : []),
        ...(timeline.status === "failed" ? ["the timeline"] : []),
    ]);

    return (
        <FoldScope value="staff-overview">
            <div className="space-y-5">
                {sentence ? (
                    <PageSentence>
                        {sentenceDetail ? <SentenceWithDetail sentence={sentence} detail={sentenceDetail} /> : sentence}
                    </PageSentence>
                ) : sentenceDetail ? (
                    // Nothing for the short line (no AI questions yet, no cap): the
                    // verdict and rating are the line, so they never go unreachable.
                    <PageSentence>{sentenceDetail}</PageSentence>
                ) : null}
                {/* The server's automatic audit of the scan on screen (#4242).
                    This is the staff Overview and a live scan only; a question
                    in the list opens its answer on Visibility. */}
                <PageActions>
                    <ScanAuditBadge
                        assessmentId={assessment?.assessment_id}
                        enabled={!isFixture && !!assessment?.assessment_id}
                        scanAt={capturedAt}
                        questionText={(n) => visModel?.rows.find((r) => r.id === n)?.text ?? null}
                        onOpenQuestion={(n) => navigate(`${ouraHref("questions", { role, businessId: business.id })}?q=${n}`)}
                    />
                </PageActions>
                {!viewingPast && <HubNextSlot />}
                <OverviewTiles
                    businessId={business.id}
                    headline={headline}
                    places={places}
                    change={change}
                    rival={rival}
                    audit={audit}
                    // Every live scan carries the readability group or says why
                    // it does not; a fixture never had one to report.
                    readabilityMeasured={!isFixture}
                    search={searchPerformance}
                    gsc={gscState}
                    month={month}
                />
                {viewingPast ? (
                    // The plan, the questions and the notes describe today, not the
                    // scan on screen, so they are not shown beside it (D11).
                    <Surface>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className={cn(TYPE.tb, "m-0 text-gray-600")}>
                                Work, questions, requests and notes show on the latest scan.
                            </p>
                            <ArrowLink onClick={onBackToLatest}>Back to the latest scan</ArrowLink>
                        </div>
                    </Surface>
                ) : (
                    <Bento layout="c21">
                        <WhatNextCard
                            businessId={business.id}
                            plan={plan}
                            asks={clientPage.status}
                            next={next}
                            recommendations={recommendations}
                            delivery={DELIVERY_ON ? delivery.load.state : null}
                            onboarding={onboarding.status}
                            nextCardCovers={!!hub?.next && !!hub.stage && (hub.stage.kind.startsWith("prospect") || hub.stage.kind === "onboarding" || hub.stage.kind === "active_unscanned")}
                        />
                        <BentoStack>
                            <AttentionCard
                                businessId={business.id}
                                rows={attention}
                                loading={sources.some((s) => s.status === "loading")}
                                unread={unread}
                            />
                            {showClientApprovals && <NotesCard businessId={business.id} notes={notes} />}
                        </BentoStack>
                    </Bento>
                )}
            </div>
        </FoldScope>
    );
}

/**
 * The header line with its verdict and rating one press away. A popover with
 * its own open state, so it opens on hover, on a click or tap (Safari does not
 * reliably focus a tapped button, so focus alone could not carry it) and on
 * Enter or Space; Escape and a press outside close it.
 */
function SentenceWithDetail({ sentence, detail }: { sentence: string; detail: string }) {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="cursor-help appearance-none rounded border-0 bg-transparent p-0 text-left font-[inherit] leading-[inherit] text-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                    // Hover is a mouse thing: on touch the tap's own pointer enter
                    // would open it and the click then toggle it shut again.
                    // A mouse click while hovering keeps it open rather than toggling it shut.
                    onPointerEnter={(e) => {
                        if (e.pointerType === "mouse") {
                            setHovered(true);
                            setOpen(true);
                        }
                    }}
                    onPointerLeave={(e) => {
                        if (e.pointerType === "mouse") {
                            setHovered(false);
                            setOpen(false);
                        }
                    }}
                    onClick={(e) => {
                        if (hovered) e.preventDefault();
                    }}
                >
                    {sentence}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                sideOffset={8}
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="w-max max-w-[min(28rem,calc(100vw-2rem))] rounded-lg border-0 bg-gray-900 px-3 py-2 text-sm font-normal text-white shadow-lg"
            >
                {detail}
            </PopoverContent>
        </Popover>
    );
}
